import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";

/**
 * Account deletion page. Required for Google Play & App Store submission.
 * Also reachable from the public web at /settings/delete-account so users
 * can request deletion without installing the app (Play policy).
 */
export default function DeleteAccount() {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const handleDelete = async () => {
    if (confirm !== "DELETE") {
      toast.error('Type DELETE to confirm');
      return;
    }
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account", {
        body: { confirm: "DELETE" },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success("Account deleted");
      await signOut();
      navigate("/", { replace: true });
    } catch (e: any) {
      toast.error(e?.message ?? "Could not delete account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate("/settings")}>
        <h1 className="mt-3 text-xl font-semibold">Delete account</h1>
      </BrandHeader>
      <main className="px-4 pb-24 pt-4 space-y-6">
        <section className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4 space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
            <div className="space-y-1">
              <h1 className="text-base font-semibold">This cannot be undone</h1>
              <p className="text-sm text-muted-foreground">
                Deleting your Cash Isle account removes your profile, cashtag,
                creator pages, and login. Your wallet must be at <strong>$0</strong>{" "}
                before deletion.
              </p>
            </div>
          </div>
        </section>

        <section className="space-y-2 text-sm text-muted-foreground">
          <h2 className="text-foreground font-medium">What we keep</h2>
          <p>
            Transaction records, payouts, and KYC verification logs are retained
            for up to 7 years to comply with Bahamian AML / KYC regulations.
            They are unlinked from your identity once your account is removed.
          </p>
          <h2 className="text-foreground font-medium pt-2">What we delete</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Display name, bio, avatar, cashtag, social links</li>
            <li>Email, phone, and login sessions</li>
            <li>Creator profile, fan list, missions, circles</li>
            <li>Push notification tokens</li>
          </ul>
        </section>

        {!session ? (
          <div className="rounded-xl border border-border bg-card p-4 text-sm">
            <p className="text-muted-foreground mb-3">
              You can also request deletion without the app by emailing{" "}
              <a className="text-primary underline" href="mailto:privacy@cashisle.app">
                privacy@cashisle.app
              </a>{" "}
              from the address on your account.
            </p>
            <Button className="w-full" onClick={() => navigate("/auth")}>
              Sign in to delete
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <Label htmlFor="confirm">Type DELETE to confirm</Label>
            <Input
              id="confirm"
              autoComplete="off"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="DELETE"
            />
            <Button
              variant="destructive"
              className="w-full"
              disabled={busy || confirm !== "DELETE"}
              onClick={handleDelete}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Permanently delete my account"}
            </Button>
          </div>
        )}
      </main>
    </MobileShell>
  );
}
